/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useEffect, useCallback } from "react";
import {
  addToCart as addToCartAPI,
  getCart as getCartAPI,
  removeFromCart as removeFromCartAPI,
  getCurrentCustomerId,
  isCustomerLoggedIn,
} from "../utils/storeApi";
import { buildImageUrl } from "../theme1/utils/theme1Helpers";

export function useSmartCart(tenantId) {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  // ================= CLEAR CART =================
  const clearCart = useCallback(() => {
    setCart([]);
    localStorage.removeItem("currentOrderId");
    localStorage.removeItem("storeCurrentOrder");
  }, []);

  // ================= REFRESH CART =================
  // داخل دالة refreshCart في useSmartCart.js
  const refreshCart = useCallback(async () => {
    if (!isCustomerLoggedIn() || !tenantId) { setCart([]); return; }

    try {
      setLoading(true);
      const res = await getCartAPI(tenantId, getCurrentCustomerId());

      if (!res?.cartData?.items) { setCart([]); return; }

      // Save orderId to localStorage so it's available for update/remove operations
      if (res.cartData.cart?.OrderId) {
        localStorage.setItem("currentOrderId", res.cartData.cart.OrderId);
      }

      // جلب المنتجات الأصلية المخزنة في المتصفح للحصول على الأسعار الصحيحة والخصومات
      const allProducts = window.__STORE_PRODUCTS__ || [];

      const mapped = res.cartData.items.flatMap(item =>
        (item.variants || []).map(v => {

           // 1. البحث عن السعر الصافي الحقيقي من قائمة المنتجات (لأن السيرفر يرجع السعر الأصلي)
           let finalNetPrice = Number(v.price || 0); // القيمة الافتراضية من السيرفر
           let originalHex = v.color;
           let currentStock = Number(v.stockQty || 0); // Default from server
           let categoryName = item.categoryName || item.category || "";
           let costPrice = Number(v.costPrice || 0); // ✅ تعريف المتغير هنا أولاً

           const productMatch = allProducts.find(p => Number(p.id) === Number(item.itemId));
           let parentImage = "";
           let variantImage = v.imageUrl;
           if (productMatch) {
             parentImage = productMatch.mainImage || productMatch.mainImageUrl || "";
             categoryName = productMatch.category || productMatch.categoryName || categoryName;
             const variantMatch = productMatch.variants?.find(vr => Number(vr.variantId) === Number(v.variantId));
             if (variantMatch) {
               const basePrice = Number(variantMatch.price || 0);
               const discountAmount = Number(variantMatch.discount || 0);
               finalNetPrice = basePrice - discountAmount;
               originalHex = variantMatch.color?.hex || variantMatch.color?.hexCode || v.color;
               variantImage = variantMatch.imageUrl || v.imageUrl;
               costPrice = Number(variantMatch.costPrice || 0); // ✅ جلب القيمة الصحيحة هنا

               const sizeMatch = variantMatch.options?.find(s => s.label === v.size);
               if (sizeMatch) {
                 currentStock = Number(sizeMatch.stock || 0);
               } else {
                 currentStock = Number(variantMatch.stockQty || 0);
               }
             }
           }

          const rawImage =
            variantImage ||
            item.imageUrl ||
            item.mainImage ||
            item.mainImageUrl ||
            parentImage;

           return {
             orderId: res.cartData.cart?.OrderId,
             itemId: item.itemId,
             variantId: v.variantId,
             name: item.itemName,
             color: originalHex,
             categoryName,
             size: v.size,
             quantity: Number(v.quantity || 1),
             // 👈 هنا نستخدم السعر الذي حسبناه (الصافي) وليس سعر السيرفر
             price: finalNetPrice,
             stockQty: currentStock, // 👈 Added stock info
             imageUrl: buildImageUrl(rawImage),
             parentImageUrl: buildImageUrl(parentImage),
             costPrice: costPrice // ✅ سعر التكلفة
           };
        })
      );
      setCart(mapped);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  // ================= ADD ITEM =================
  // useSmartCart.js
  // داخل useSmartCart.js
  // داخل useSmartCart.js

  // 1. تعديلaddItem لضمان إرسال الصافي
  const addItem = useCallback(async (itemData, qtyToAdd = 1) => {
    const customerId = getCurrentCustomerId();
    if (!itemData?.variantId || !customerId || !tenantId) return { success: false };

    // Check stock if available in itemData
    if (itemData.stockQty !== undefined && qtyToAdd > itemData.stockQty) {
      return { success: false, error: "الكمية المطلوبة غير متوفرة في المخزون" };
    }

    try {
      const res = await addToCartAPI(
        {
          variantId: itemData.variantId,
          // السعر الممرر من البطاقة هو أصلاً netPrice
          price: Number(itemData.price),
          discount: Number(itemData.discount || 0),
          color: itemData.color,
          size: itemData.size || ""
        },
        qtyToAdd,
        tenantId,
        customerId
      );

      if (res?.success) {
        await refreshCart();
        return { success: true };
      }
      return { success: false, error: res?.message || "فشل إضافة المنتج" };
    } catch (err) {
      return { success: false, error: "حدث خطأ أثناء الإضافة للسلة" };
    }
  }, [tenantId, refreshCart]);

  // 2. تعديل updateQuantity ليرسل السعر المخزن في السلة (الصافي) وليس السعر الأصلي
  const updateQuantity = useCallback(async (variantId, delta) => {
    const item = cart.find(x => Number(x.variantId) === Number(variantId));
    if (!item) return { success: false };

    if (delta > 0 && item.quantity + delta > item.stockQty) {
      return { success: false, error: "لقد وصلت للحد الأقصى المتوفر في المخزون" };
    }

    const customerId = getCurrentCustomerId();
    const orderId = localStorage.getItem("currentOrderId");

    try {
      if (item.quantity === 1 && delta === -1) {
        await removeFromCartAPI(tenantId, orderId, variantId, customerId);
      } else {
        // 👈 التعديل هنا: نرسل item.price (الذي هو الصافي المخزن في السلة)
        await addToCartAPI(
          {
            variantId,
            price: Number(item.price), // الصافي
            discount: 0, // لا نرسل الخصم مرة أخرى لأنه مخصوم أصلاً من السعر
            color: item.color,
            size: item.size || ""
          },
          delta,
          tenantId,
          customerId
        );
      }

      await refreshCart();
      return { success: true };
    } catch {
      return { success: false, error: "حدث خطأ أثناء تحديث الكمية" };
    }
  }, [cart, tenantId, refreshCart]);

  // ================= REMOVE ITEM =================
  const removeItem = useCallback(async (variantId) => {
    const customerId = getCurrentCustomerId();
    const orderId = localStorage.getItem("currentOrderId");

    try {
      await removeFromCartAPI(tenantId, orderId, variantId, customerId);
      await refreshCart();
      return { success: true };
    } catch {
      return { success: false };
    }
  }, [tenantId, refreshCart]);

  return { cart, loading, addItem, updateQuantity, removeItem, refreshCart, clearCart };
}
